import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, tarotApi } from '../lib/api';
import { ThemeBackdrop } from '../lib/themeEmblem';
import { useTheme } from '../lib/useTheme';

/**
 * /session/:id/paywall — v2 REPLACE per spec §5.
 *
 * 設計決策：
 *   - 主要 paywall UX 已 inline 在 Session.tsx（從 composer 「長出來」）
 *   - 這個獨立 route 仍保留作為「直接連進 / 分享 URL / reload 後重新落地」的入口
 *   - 視覺上跟 inline panel 同套 chrome，避免使用者覺得「跳到不同畫面」
 *   - 核心 UX：在主視覺內顯示同樣的 paywall panel；解鎖成功 → 回 session
 *   - 移除 ✦ emoji；文案走 spec §6（「Send me the full report」/ trust 句）
 *   - 不寫死英文 — 全 i18n
 *
 * Mock-friendly：付款 API 失敗 → inline error；網路錯（後端沒跑）→ demo 直接放行。
 */

export default function Paywall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();

  const [email, setEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (!id || paying) return;
    setPaying(true);
    setError(null);
    try {
      await tarotApi.pay(id);
      navigate(`/session/${id}`);
    } catch (e) {
      console.warn('[paywall] pay failed', e);
      if (e instanceof ApiError) {
        setError(t('paywall.errors.api'));
      } else {
        // 網路錯（後端沒跑）→ demo 模式直接放行
        navigate(`/session/${id}`);
        return;
      }
    } finally {
      setPaying(false);
    }
  }

  function endHere() {
    if (!id) return;
    navigate(`/session/${id}/report`);
  }

  return (
    <main className="relative isolate flex min-h-screen flex-col">
      <ThemeBackdrop theme={theme} />

      <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-1 flex-col px-6 pb-12 pt-24 sm:pt-28">
        <Link
          to={`/session/${id ?? ''}`}
          className="mb-8 inline-block text-sm text-muted hover:text-fg"
        >
          ← {t('common.nav.back')}
        </Link>

        {/* Editorial header — 不是大字行銷詞，是「對話延續」的口吻 */}
        <header className="mb-10">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('paywall.kicker')}
          </p>
          <h1
            className="text-balance font-display text-4xl leading-tight text-heading sm:text-5xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('paywall.title')}
          </h1>
          <p className="mt-4 max-w-lg text-pretty text-base leading-relaxed text-muted">
            {t('paywall.subtitle')}
          </p>
        </header>

        {/* Inline panel — 視覺上跟 Session 內 inline panel 一致 */}
        <form
          onSubmit={handleUnlock}
          className="space-y-5 p-6 sm:p-8"
          style={{
            background: 'var(--color-paywall-bg)',
            border: '1px solid var(--color-paywall-border)',
            borderRadius: 'var(--radius-card)',
          }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-display text-2xl text-heading">{t('paywall.panelTitle')}</h2>
            <span className="font-display text-3xl text-accent">{t('paywall.price')}</span>
          </div>

          <p className="text-pretty text-sm leading-relaxed text-muted">
            {t('paywall.body')}
          </p>

          <div
            className="flex items-center gap-2 px-3 py-2"
            style={{
              background: 'var(--color-composer-bg)',
              border: '1px solid var(--color-composer-border)',
              borderRadius: 'var(--radius-pill)',
            }}
          >
            <EnvelopeIcon />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('paywall.emailPlaceholder')}
              className="flex-1 bg-transparent px-1 py-1 text-sm text-fg outline-none placeholder:text-muted"
              aria-label={t('paywall.emailPlaceholder')}
            />
          </div>

          {error && (
            <p role="alert" className="text-pretty text-xs text-accent">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={paying || !email.trim()}
            className="flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              background: 'var(--btn-bg)',
              color: 'var(--btn-ink)',
              border: '1px solid var(--btn-border, transparent)',
              borderRadius: 'var(--radius-pill)',
              boxShadow: 'var(--btn-shadow)',
            }}
          >
            {paying ? t('paywall.processing') : t('paywall.cta')}
          </button>

          <p className="text-center font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted">
            {t('paywall.trust')}
          </p>
        </form>

        <button
          type="button"
          onClick={endHere}
          className="mt-8 self-center text-sm text-muted underline-offset-4 hover:text-fg hover:underline"
        >
          {t('paywall.endHere')}
        </button>
      </div>
    </main>
  );
}

function EnvelopeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.8"
      />
      <path
        d="M3 7l9 6 9-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.8"
      />
    </svg>
  );
}
